import _ from 'lodash'
import { getGameThumbnailUrl } from '@/utils/gameThumbnails'

const state = {
    // Object of cache keys to cached data/promises
    cache: {},
    // Object to track loading states for UI indicators
    loadingKeys: {},
    // Separate cache for histogram data
    histogramCache: {},
    histogramLoadingKeys: {},
}

const getters = {
        /**
         * Get cached filter data by cache key
         */
        getCachedFilterData: (state) => (cacheKey) => {
            return state.cache[cacheKey]
        },

        /**
         * Check if a filter request is currently loading
         */
        isFilterLoading: (state) => (cacheKey) => {
            return !!state.loadingKeys[cacheKey]
        },

        /**
         * Get cache statistics for debugging
         */
        getCacheStats: (state) => {
            return {
                size: Object.keys(state.cache).length,
                loadingCount: Object.keys(state.loadingKeys).length,
                keys: Object.keys(state.cache),
                histogramSize: Object.keys(state.histogramCache).length,
                histogramLoadingCount: Object.keys(state.histogramLoadingKeys).length,
                histogramKeys: Object.keys(state.histogramCache),
            }
        },

        /**
         * Get cached histogram data by cache key
         */
        getCachedHistogramData: (state) => (cacheKey) => {
            return state.histogramCache[cacheKey]
        },

        /**
         * Check if a histogram request is currently loading
         */
        isHistogramLoading: (state) => (cacheKey) => {
            return !!state.histogramLoadingKeys[cacheKey]
        },
}

const mutations = {
        SET_CACHED_DATA(state, { cacheKey, data }) {
            state.cache[cacheKey] = data
        },

        SET_CACHED_PROMISE(state, { cacheKey, promise }) {
            state.cache[cacheKey] = promise
        },

        DELETE_CACHED_DATA(state, cacheKey) {
            delete state.cache[cacheKey]
        },

        CLEAR_CACHE(state) {
            state.cache = {}
        },

        SET_LOADING(state, cacheKey) {
            state.loadingKeys[cacheKey] = true
        },

        CLEAR_LOADING(state, cacheKey) {
            delete state.loadingKeys[cacheKey]
        },

        SET_CACHED_HISTOGRAM(state, { cacheKey, data }) {
            state.histogramCache[cacheKey] = data
        },

        SET_CACHED_HISTOGRAM_PROMISE(state, { cacheKey, promise }) {
            state.histogramCache[cacheKey] = promise
        },

        DELETE_CACHED_HISTOGRAM(state, cacheKey) {
            delete state.histogramCache[cacheKey]
        },

        SET_HISTOGRAM_LOADING(state, cacheKey) {
            state.histogramLoadingKeys[cacheKey] = true
        },

        CLEAR_HISTOGRAM_LOADING(state, cacheKey) {
            delete state.histogramLoadingKeys[cacheKey]
        },

        CLEAR_HISTOGRAM_CACHE(state) {
            state.histogramCache = {}
        },
}

const actions = {
        /**
         * Generate cache key from filter parameters
         */
        generateCacheKey(context, params) {
            return JSON.stringify({
                platform: params.platform,
                codeType: params.codeType,
                contentType: params.contentType,
                dayRange: params.dayRange,
                classificationsTopic: params.classificationsTopic,
                classificationsCategory: params.classificationsCategory,
                sort: params.sort,
            })
        },

        /**
         * Fetch filter codes with caching
         */
        async fetchFilterCodes({ commit, getters, dispatch }, params) {
            const { platform, codeType, currentFiltersContext, displayOptions, searchText } = params

            // Don't cache search queries
            if (searchText) {
                return dispatch('makeFilterRequest', params)
            }

            // Generate cache key
            const cacheKey = JSON.stringify({
                platform,
                codeType,
                contentType: currentFiltersContext?.content_type,
                dayRange: currentFiltersContext?.day_range,
                classificationsTopic: currentFiltersContext?.classifications?.hierarchy?.topic,
                classificationsCategory: currentFiltersContext?.classifications?.hierarchy?.category,
                sort: displayOptions?.sort,
            })

            // Check if we already have cached data
            const cachedValue = getters.getCachedFilterData(cacheKey)
            if (cachedValue) {
                // If it's a promise (ongoing request), wait for it
                if (cachedValue instanceof Promise) {
                    commit('SET_LOADING', cacheKey)
                    try {
                        const result = await cachedValue
                        commit('CLEAR_LOADING', cacheKey)
                        return result
                    } catch (error) {
                        commit('CLEAR_LOADING', cacheKey)
                        throw error
                    }
                }
                // If it's actual data, return immediately
                return cachedValue
            }

            // Create a promise for the API request
            const requestPromise = dispatch('makeFilterRequest', { ...params, cacheKey })

            // Store the promise in cache so concurrent requests can wait for it
            commit('SET_CACHED_PROMISE', { cacheKey, promise: requestPromise })
            commit('SET_LOADING', cacheKey)

            try {
                const result = await requestPromise
                // Replace the promise with the actual result
                commit('SET_CACHED_DATA', { cacheKey, data: result })
                commit('CLEAR_LOADING', cacheKey)
                return result
            } catch (error) {
                // Remove failed promise from cache
                commit('DELETE_CACHED_DATA', cacheKey)
                commit('CLEAR_LOADING', cacheKey)
                throw error
            }
        },

        /**
         * Make the actual API request
         */
        async makeFilterRequest({ dispatch }, params) {
            const { platform, codeType, currentFiltersContext, displayOptions, searchText } = params

            // Cancel any pending requests for this endpoint
            await dispatch('CANCEL_PENDING_REQUESTS_BY_ENDPOINT', `/discovery/${platform}/codes/${codeType}`)

            const sort = []
            if (displayOptions?.sort) {
                sort.push({
                    field: displayOptions.sort.field,
                    direction: displayOptions.sort.direction ?? 'desc',
                })
            }

            const filters = [{
                id: 'platform_id',
                value: platform,
            }, {
                id: 'content_type',
                value: currentFiltersContext?.content_type,
            }, {
                id: 'day_range',
                value: currentFiltersContext?.day_range,
            }]

            if (searchText) {
                filters.push({
                    id: 'label',
                    value: searchText,
                })
            }

            if (['classifications_category'].includes(codeType)) {
                filters.push({
                    id: 'parent',
                    value: [currentFiltersContext.classifications.hierarchy.topic]
                })
            }

            if (['classifications_subcategory'].includes(codeType)) {
                filters.push({
                    id: 'parent',
                    value: [
                        currentFiltersContext.classifications.hierarchy.topic,
                        currentFiltersContext.classifications.hierarchy.category
                    ]
                })
            }

            try {
                const codesResponse = await this.$axios.post(`/discovery/${platform}/codes/${codeType}`, {
                    filters,
                    pagination: {
                        limit: 50,
                        skip: 0,
                        sort,
                    },
                    is_count_enabled: false // Disable count query for better performance
                })

                // Process the response data
                return dispatch('processFilterResponse', {
                    codesResponse,
                    codeType,
                    platform,
                    currentFiltersContext,
                    searchText,
                })
            } catch (error) {
                console.error(`fetchFilterCodes -> Error -> ${searchText}`, error)
                throw error
            }
        },

        /**
         * Process the API response into the expected format
         */
        processFilterResponse(context, { codesResponse, codeType, platform, currentFiltersContext, searchText }) {
            // Add special "All" options only for specific classification types
            if (!searchText && codeType === 'classifications_topic') {
                const allOption = {
                    id: null,
                    code: null,
                    label: 'All niches',
                    emoji: '🏭',
                }
                codesResponse.data.payload.codes.unshift(allOption)
            }

            if (!searchText && codeType === 'classifications_category' && currentFiltersContext?.classifications?.hierarchy?.topic) {
                const allOption = {
                    id: null,
                    code: null,
                    label: 'All Categories',
                    emoji: '🏷️',
                }
                codesResponse.data.payload.codes.unshift(allOption)
            }

            if (codeType === 'alternate_platforms') {
                codesResponse.data.payload.codes = _.filter(codesResponse.data.payload.codes, ({ code }) => {
                    return code !== platform
                })
            }

            // Transform the codes into the expected format
            return _.map(codesResponse.data.payload.codes, (code) => {
                // Only calculate metric if content_type and day_range are available
                let metric = 0
                if (currentFiltersContext?.content_type && currentFiltersContext?.day_range) {
                    metric = _.get(code, `metrics.platforms.${platform}.${currentFiltersContext.content_type}.day_${currentFiltersContext.day_range}`, 0)
                }

                const result = {
                    id: code.code,
                    label: code.label,
                    emoji: code.emoji,
                    metric,
                }

                // Preserve _platforms field for IGDB games
                if (code._platforms) {
                    result._platforms = code._platforms
                }

                if (codeType === 'games_list') {
                    const thumbnailPlatform = code.thumbnail_platform || platform
                    const thumbnailGameId = code.thumbnail_game_id || code.code
                    const thumbnailSize = thumbnailPlatform === 'igdb' ? 'small' : 'default'
                    const thumbnailUrl = code.thumbnail_url || getGameThumbnailUrl({
                        platform: thumbnailPlatform,
                        gameId: thumbnailGameId,
                        thumbnailSize,
                    })

                    result.background_url = thumbnailUrl
                    result.thumbnail_url = code.thumbnail_url || null
                    result.thumbnail_platform = code.thumbnail_platform || null
                    result.thumbnail_game_id = code.thumbnail_game_id || null

                    if (code?.metadata?.release_year) {
                        _.set(result, 'metadata.release_year', code.metadata.release_year)
                    }
                }

                return result
            })
        },

        /**
         * Fetch histogram data with caching
         */
        async fetchHistogramData({ commit, getters }, { platformId, fieldKey }) {
            // Generate cache key for histogram
            const cacheKey = JSON.stringify({
                platform: platformId,
                fieldKey,
                type: 'histogram'
            })

            // Check if we already have cached data
            const cachedValue = getters.getCachedHistogramData(cacheKey)
            if (cachedValue) {
                // If it's a promise (ongoing request), wait for it
                if (cachedValue instanceof Promise) {
                    commit('SET_HISTOGRAM_LOADING', cacheKey)
                    try {
                        const result = await cachedValue
                        commit('CLEAR_HISTOGRAM_LOADING', cacheKey)
                        return result
                    } catch (error) {
                        commit('CLEAR_HISTOGRAM_LOADING', cacheKey)
                        throw error
                    }
                }
                // If it's actual data, return immediately
                return cachedValue
            }

            // Create a promise for the API request
            const requestPromise = (async () => {
                try {
                    const { data } = await this.$axios.get(`/discovery/${platformId}/histograms/${fieldKey}`)
                    const histogram = data.payload.histogram || null

                    // Replace the promise with the actual result
                    commit('SET_CACHED_HISTOGRAM', { cacheKey, data: histogram })
                    return histogram
                } catch (error) {
                    console.error(`[${fieldKey}] Error fetching histogram`, error)
                    commit('DELETE_CACHED_HISTOGRAM', cacheKey)
                    return null
                }
            })()

            // Store the promise in cache so concurrent requests can wait for it
            commit('SET_CACHED_HISTOGRAM_PROMISE', { cacheKey, promise: requestPromise })
            commit('SET_HISTOGRAM_LOADING', cacheKey)

            try {
                const result = await requestPromise
                commit('CLEAR_HISTOGRAM_LOADING', cacheKey)
                return result
            } catch (error) {
                commit('CLEAR_HISTOGRAM_LOADING', cacheKey)
                throw error
            }
        },

        /**
         * Fetch histograms for multiple fields (used when expanding a group)
         */
        async fetchHistogramBatch({ dispatch }, { platformId, fieldKeys }) {
            const histogramPromises = fieldKeys.map(fieldKey =>
                dispatch('fetchHistogramData', { platformId, fieldKey })
                    .then(data => ({ fieldKey, data }))
                    .catch(error => ({ fieldKey, data: null, error }))
            )

            const results = await Promise.all(histogramPromises)

            // Convert array back to object format expected by generateStyleCache
            const histogramsByField = {}
            results.forEach(({ fieldKey, data }) => {
                // Store the data in the format expected by generateStyleCache
                if (data) {
                    histogramsByField[fieldKey] = data
                } else {
                    histogramsByField[fieldKey] = null
                }
            })

            return histogramsByField
        },

        /**
         * Clear all cached data (useful for testing or manual cache invalidation)
         */
        clearFilterCache({ commit }) {
            commit('CLEAR_CACHE')
            commit('CLEAR_HISTOGRAM_CACHE')
        },
}

export default {
    state,
    mutations,
    actions,
    getters,
}
